/**
 * @author: lv.chen
 * @date: 2025/8/7
 * @desc: AddMarketReport组件，新增市场报告组件
 */
import React, { useRef, useEffect } from 'react';
import classnames from 'classnames';
import { history, useIntl, useSelector, useDispatch } from 'umi';
import { cloneDeep } from 'lodash-es';
import { Layout } from 'tea-component';
import Loading from '@home/components/Loading';
import ReportForm, { IReportFormInstance } from './components/ReportForm';
import usePageLog, { reportButtonLog } from '@user-center/_hooks/usePageLog';
import useReportAdminMode from '../_hooks/useReportAdminMode';
import useReportOptions from '../_hooks/useReportOptions';
import useReportPublish from '../_hooks/useReportPublish';
import { addReport, updateReport, handleAddUpdateReportError } from '../services';
import { storage } from '@/utils/utils';
import { REPORT_DATA_MODEL_NAMESPACE, IReportDataState } from '@home/models/report';
import { ReportState } from '@user-center/enums/reports';
import { DEFAULT_REPORT } from '../constants';
import { getReportMutationParams } from '../utils/reportParams';

import '../index.scss';

const { Body, Content } = Layout;

interface IAddMarketReportProps extends IBaseProps {}

const AddMarketReport: React.FC<IAddMarketReportProps> = (props) => {
  const { style, className } = props;

  const { locale, formatMessage } = useIntl();

  const { loading: adminModeLoading, adminMode } = useReportAdminMode();

  const dispatch = useDispatch();

  const { recognizeResult } = useSelector<any, IReportDataState>(
    (rootState) => rootState?.[REPORT_DATA_MODEL_NAMESPACE] || {},
  );

  const { permissionOptions } = useReportOptions(locale);

  const reportFormInstance = useRef<IReportFormInstance | null>(null);

  const report = useRef<IReport | null>(null);

  const { reportPublishLoading, onReportPublish } = useReportPublish(adminMode, report);

  useEffect(() => {
    if (adminModeLoading || !reportFormInstance.current) {
      return;
    }

    const openId = storage.getItem('openId', 'sessionStorage')?.trim() ?? '';
    reportFormInstance.current?.updateCreatorInformation(openId);

    if (!!recognizeResult) {
      const recognizeResultFile = recognizeResult.file as File;
      const recognizeResultResult = recognizeResult.result;
      reportFormInstance.current?.initializeWhenRecognizedReport(recognizeResultFile, recognizeResultResult);
      dispatch({ type: `${REPORT_DATA_MODEL_NAMESPACE}/updateReportRecognizeResult`, payload: null });
    }
  }, [adminModeLoading]);

  usePageLog('contentManagementCenterReportsAdd');

  if (adminModeLoading) {
    return <Loading />;
  }

  return (
    <Layout style={style} className={classnames('dbu-add-report-layout', className)}>
      <Body>
        <Content>
          <Content.Header
            showBackButton
            title={formatMessage({ id: 'dbu_common_report' })}
            onBackButtonClick={(e) => {
              e?.stopPropagation();
              history.goBack();
            }}
          />
          <Content.Body>
            <ReportForm
              className="dbu-add-report-form"
              ref={reportFormInstance}
              clearOnRecognitionError
              reportPublishLoading={reportPublishLoading}
              permissionOptions={permissionOptions}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onConfirm={async (reportPayload: IReportPartial) => {
                let success = false;
                try {
                  if (!!report.current?.resource_id) {
                    const reportNew = {
                      ...DEFAULT_REPORT,
                      ...report.current,
                      ...reportPayload,
                      state: report.current?.state ?? ReportState.UnPublished,
                    };
                    await updateReport(reportNew, getReportMutationParams(adminMode, reportNew));
                    report.current = cloneDeep(reportNew);
                    reportFormInstance.current?.preview(cloneDeep(reportNew));
                  } else {
                    const reportToAdd = {
                      ...DEFAULT_REPORT,
                      ...reportPayload,
                    };
                    const res = await addReport(reportToAdd, getReportMutationParams(adminMode, reportToAdd));
                    const reportNew = res?.data;
                    if (!reportNew) {
                      throw new Error('Market report creation did not return report data.');
                    }
                    report.current = cloneDeep(reportNew);
                    reportFormInstance.current?.preview(cloneDeep(reportNew));
                  }
                  success = true;
                } catch (error) {
                  handleAddUpdateReportError(formatMessage, error);
                  console.error(error);
                }
                reportButtonLog('contentManagementCenterReportsAddSaveSubmit');
                return success;
              }}
              onBack={() => {
                reportButtonLog('contentManagementCenterReportsAddBackSubmit');
              }}
              onPreview={() => {
                reportButtonLog('contentManagementCenterReportsAddPreviewSubmit');
              }}
              onPublish={(payload: IReportPartial) => {
                onReportPublish(payload);
                reportButtonLog('contentManagementCenterReportsAddShareSubmit');
              }}
              onUploadCover={() => {
                reportButtonLog('contentManagementCenterReportsAddUploadCoverSubmit');
              }}
            />
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
};

export default AddMarketReport;
